import { BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
/**
 * PostHog Analytics Integration
 * Comprehensive tracking for user interactions, conversions, and session recording
 *
 * Brand project: **422815** (Ana Fernandez) — see posthogBrandAlignment.ts
 */

import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";
import type { PostHogRouteMeta } from "./posthogRouteMeta";
import {
  BRAND_POSTHOG_PROJECT_ID,
  BRAND_POSTHOG_UI_HOST,
  brandPostHogIdentityProps,
  isBrandPostHogReplayUrl,
  isLikelyPostHogSessionId,
  resolveClientPostHogProjectId,
} from "./posthogBrandAlignment";

// PostHog configuration check
export const isPostHogConfigured = () => {
  return (
    typeof window !== "undefined" &&
    window.posthog &&
    typeof window.posthog.capture === "function"
  );
};

/** Canonical UI project (**422815**) from Railway VITE_POSTHOG_PROJECT_ID. */
const POSTHOG_UI_PROJECT_ID = resolveClientPostHogProjectId();

/** UI host from Railway VITE_POSTHOG_UI_HOST — never ingest proxy / us.i. */
const POSTHOG_UI_HOST_CANONICAL = (() => {
  const raw = String(
    import.meta.env.VITE_POSTHOG_UI_HOST ||
      import.meta.env.NEXT_PUBLIC_POSTHOG_UI_HOST ||
      BRAND_POSTHOG_UI_HOST,
  )
    .trim()
    .replace(/\/+$/, "");
  if (!raw || raw.startsWith("/") || raw.includes("i.posthog") || raw.includes("/ingest")) {
    return BRAND_POSTHOG_UI_HOST;
  }
  return raw;
})();

/**
 * Build a clickable Session Replay URL for the brand PostHog project.
 * Only accepts real PostHog `$session_id` (UUID v7). Never builds links from
 * DualCTA `crypto.randomUUID()` flow ids — those 404 in the UI.
 * Never trust SDK urls that point at `/ingest` or use `phc_…` as project id.
 */
export function buildCanonicalPostHogReplayUrl(
  sessionId: string | null | undefined,
): string | null {
  const sid = String(sessionId ?? "").trim();
  if (!isLikelyPostHogSessionId(sid)) return null;
  return `${POSTHOG_UI_HOST_CANONICAL}/project/${POSTHOG_UI_PROJECT_ID}/replay/${encodeURIComponent(sid)}`;
}

export { isLikelyPostHogSessionId };

/** True when URL is a deep-link into us.posthog.com/project/422815/replay/… only. */
export function isCanonicalPostHogReplayUrl(url: string | null | undefined): boolean {
  return isBrandPostHogReplayUrl(url);
}

/**
 * `main.tsx` dispara este evento al final del callback `loaded` de `posthog.init`.
 * Así el primer `$pageview` / `route_change` no corre contra un SDK a medio cargar
 * (el wizard de PostHog y Live events suelen esperar el primer evento estable).
 */
export const POSTHOG_CLIENT_READY_EVENT = "posthog:client-ready";

/** Ejecuta el callback ya si `__loaded`, o una vez cuando el cliente esté listo. */
export function subscribePostHogClientReady(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  const ph0 = window.posthog as { __loaded?: boolean; capture?: unknown } | undefined;
  if (!ph0 || typeof ph0.capture !== "function") {
    return () => {};
  }

  const run = () => {
    try {
      callback();
    } catch {
      // ignore
    }
  };

  if (ph0.__loaded) {
    queueMicrotask(run);
    return () => {};
  }

  const onReady = () => run();
  window.addEventListener(POSTHOG_CLIENT_READY_EVENT, onReady, { once: true });
  return () => {
    window.removeEventListener(POSTHOG_CLIENT_READY_EVENT, onReady);
  };
}

// ─── Funnel mirror → PostHog (same event_name + props as /api/track-funnel-event) ─

const FUNNEL_MIRROR_PH_QUEUE_MAX = 100;

type QueuedFunnelMirrorCapture = { eventName: string; properties: Record<string, unknown> };

const funnelMirrorPhQueue: QueuedFunnelMirrorCapture[] = [];
let funnelMirrorPhFlushHooked = false;
let funnelMirrorPhStaleTimer: ReturnType<typeof setTimeout> | null = null;

function flushFunnelMirrorPhQueue(): void {
  if (funnelMirrorPhStaleTimer !== null && typeof window !== "undefined") {
    window.clearTimeout(funnelMirrorPhStaleTimer);
    funnelMirrorPhStaleTimer = null;
  }
  while (funnelMirrorPhQueue.length > 0) {
    const item = funnelMirrorPhQueue.shift();
    if (!item) break;
    if (!isPostHogConfigured()) continue;
    try {
      posthog.capture(item.eventName, item.properties);
    } catch {
      /* noop */
    }
  }
}

function ensureFunnelMirrorPhFlushHook(): void {
  if (typeof window === "undefined" || funnelMirrorPhFlushHooked) return;
  funnelMirrorPhFlushHooked = true;
  window.addEventListener(
    POSTHOG_CLIENT_READY_EVENT,
    () => {
      flushFunnelMirrorPhQueue();
    },
    { passive: true },
  );
}

function queueFunnelMirrorPhCapture(eventName: string, properties: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  ensureFunnelMirrorPhFlushHook();
  if (funnelMirrorPhQueue.length >= FUNNEL_MIRROR_PH_QUEUE_MAX) funnelMirrorPhQueue.shift();
  funnelMirrorPhQueue.push({ eventName, properties });
  if (funnelMirrorPhStaleTimer === null) {
    funnelMirrorPhStaleTimer = window.setTimeout(() => {
      funnelMirrorPhStaleTimer = null;
      flushFunnelMirrorPhQueue();
    }, 20_000);
  }
}

/**
 * Captures the same funnel event in PostHog as stored via `mirrorFunnelEvent` → `/api/track-funnel-event`.
 * Queues until the JS client is ready when capture runs before `posthog:client-ready`.
 */
export function captureFunnelMirrorPayloadPostHog(enrichedPayload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;

  const eventName = enrichedPayload.event_name;
  if (typeof eventName !== "string" || !eventName.trim()) return;

  const propsObj =
    enrichedPayload.props &&
    typeof enrichedPayload.props === "object" &&
    !Array.isArray(enrichedPayload.props)
      ? { ...(enrichedPayload.props as Record<string, unknown>) }
      : {};

  const sid = enrichedPayload.session_id;
  if (
    (sid !== undefined && sid !== null && sid !== "") &&
    (propsObj.funnel_session_id === undefined || propsObj.funnel_session_id === null || propsObj.funnel_session_id === "")
  ) {
    propsObj.funnel_session_id = sid;
  }

  const passKeys = [
    "customer_email",
    "template",
    "template_variant",
    "flow_name",
    "landing_path",
    "country",
    "language",
    "page_url",
  ] as const;

  for (const key of passKeys) {
    const v = enrichedPayload[key];
    if (v !== undefined && v !== null && v !== "" && propsObj[key] === undefined) {
      propsObj[key] = v;
    }
  }

  if (isPostHogConfigured()) {
    try {
      posthog.capture(eventName, propsObj);
    } catch {
      /* noop */
    }
    return;
  }

  queueFunnelMirrorPhCapture(eventName, propsObj);
}

// ─── Client hints for person profile (not set by PostHog JS by default) ─────

/** Instagram / Facebook / TikTok in-app browsers, or Android System WebView (`; wv)`). */
function detectInAppBrowserLabel(): string | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/\bFBAN\b|\bFBAV\b|FB_IAB|FB4A|FBIOS/i.test(ua)) return "facebook";
  if (/TikTok/i.test(ua)) return "tiktok";
  if (/Line\//i.test(ua)) return "line";
  if (/LinkedInApp/i.test(ua)) return "linkedin";
  if (/Twitter/i.test(ua)) return "twitter";
  if (/; wv\).*Chrome/i.test(ua)) return "android_webview";
  return null;
}

// ─── UTM helpers (shared) ─────────────────────────────────────────────────────

/**
 * PostHog JS automatically captures UTM params and sets them as person
 * properties under standard names:
 *   last-touch  → utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *   first-touch → $initial_utm_source, $initial_utm_medium, ...
 * We only parse UTMs here to fire our custom utm_attribution event.
 * See: https://posthog.com/docs/product-analytics/person-properties#default-person-properties
 */
function parseUTMFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const keys = [
    "utm_source", "utm_medium", "utm_campaign",
    "utm_term", "utm_content", "utm_id",
    "gclid", "fbclid", "ttclid", "msclkid", "ref",
  ];
  const result: Record<string, string> = {};
  for (const key of keys) {
    const val = params.get(key);
    if (val) result[key] = val;
  }
  return result;
}

// ─── Super Properties ─────────────────────────────────────────────────────────

/**
 * Register super properties that PostHog automatically attaches to EVERY event.
 * Call once after PostHog loads. Avoids copy-pasting user_region/test_country
 * across every individual capture() call.
 */
export function registerSuperPropertiesPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.register({
    ...brandPostHogIdentityProps(),
    // brandPostHogIdentityProps already sets site/brand/project/primary_product
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
    app_version: import.meta.env.VITE_APP_VERSION || null,
    build_mode: import.meta.env.MODE,
    is_production: import.meta.env.PROD,
  });

  // Geo + hero CTA from Worker inject (CABA none, province community, …).
  // Deferred import avoids circular deps with mirrorFunnelEvent.
  try {
    void import("@/lib/homeCtaPosthogTracking").then((m) => {
      m.registerHomeCtaSuperProperties();
    });
  } catch {
    /* noop */
  }
}

// ─── Global event enrichment (before_send) ───────────────────────────────────

let cachedNavigationType: string | null | undefined;

function getNavigationTypeCached(): string | null {
  if (cachedNavigationType !== undefined) return cachedNavigationType;
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    cachedNavigationType = nav?.type ?? null;
  } catch {
    cachedNavigationType = null;
  }
  return cachedNavigationType;
}

/** Counts URL search keys and whether UTMs are present — no query values (privacy). */
function getUrlSearchTelemetry(): {
  key_count: number;
  has_utm: boolean;
  has_gclid: boolean;
  has_fbclid: boolean;
} {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = [...params.keys()];
    let has_utm = false;
    let has_gclid = false;
    let has_fbclid = false;
    for (const k of keys) {
      const lower = k.toLowerCase();
      if (lower.startsWith("utm_")) has_utm = true;
      if (lower === "gclid") has_gclid = true;
      if (lower === "fbclid") has_fbclid = true;
    }
    return {
      key_count: keys.length,
      has_utm,
      has_gclid,
      has_fbclid,
    };
  } catch {
    return { key_count: 0, has_utm: false, has_gclid: false, has_fbclid: false };
  }
}

/**
 * Merged into every outbound event via `before_send` in `posthog.init`.
 * Keeps correlation IDs and environment context consistent for HogQL, funnels, and MCP.
 * Does not attach raw query strings or PII.
 *
 * Always stamps brand + project **422815** + primary product (vip membership)
 * + a *canonical* session-replay deep-link (us.posthog.com/project/422815/…).
 * SDK with api_host=/ingest otherwise emits `/ingest/project/phc_…/replay/…`
 * which 404s in the PostHog UI and breaks Slack.
 */
export function posthogBeforeSendEnrich(
  cr: CaptureResult | null,
): CaptureResult | null {
  if (!cr || typeof window === "undefined") return cr;

  const extra: Record<string, unknown> = {};
  const props = cr.properties ?? {};

  const ph = window.posthog as
    | {
        get_distinct_id?: () => string;
        get_session_id?: () => string;
        version?: string;
      }
    | undefined;

  try {
    if (props.client_distinct_id === undefined && ph?.get_distinct_id) {
      extra.client_distinct_id = ph.get_distinct_id();
    }
  } catch {
    /* ignore */
  }

  let sessionId: string | null = null;
  try {
    sessionId = ph?.get_session_id?.() ?? null;
    if (props.client_session_id === undefined && sessionId) {
      extra.client_session_id = sessionId;
    }
  } catch {
    /* ignore */
  }

  if (props.build_mode === undefined) {
    extra.build_mode = import.meta.env.MODE;
  }
  if (props.is_production === undefined) {
    extra.is_production = import.meta.env.PROD;
  }
  if (props.brand_host === undefined) {
    extra.brand_host = BRAND_SITE_HOST;
  }
  if (props.site === undefined) {
    extra.site = BRAND_SOURCE_KEY;
  }
  if (props.brand === undefined) {
    extra.brand = BRAND_SOURCE_KEY;
  }
  // Always stamp numeric project id (canonical 422815 for this brand).
  extra.posthog_project_id = POSTHOG_UI_PROJECT_ID || BRAND_POSTHOG_PROJECT_ID;
  if (props.primary_product === undefined) {
    extra.primary_product = "vip";
  }
  if (props.primary_product_type === undefined) {
    extra.primary_product_type = "membership";
  }
  if (props.posthog_js_version === undefined && ph?.version) {
    extra.posthog_js_version = ph.version;
  }

  // Canonical replay URL only for real PostHog $session_id (UUID v7).
  // Never keep /ingest, phc_, or DualCTA flow-trace (UUID v4) dead links.
  const existingReplay =
    typeof props.ph_session_replay_url === "string"
      ? props.ph_session_replay_url
      : null;
  if (!isCanonicalPostHogReplayUrl(existingReplay)) {
    const sid =
      sessionId ||
      (typeof props.client_session_id === "string" ? props.client_session_id : null);
    const built = buildCanonicalPostHogReplayUrl(sid);
    if (built) {
      extra.ph_session_replay_url = built;
    } else if (existingReplay) {
      extra.ph_session_replay_url = null;
    }
  }

  const navType = getNavigationTypeCached();
  if (navType && props.navigation_type === undefined) {
    extra.navigation_type = navType;
  }

  try {
    if (props.visibility_state === undefined) {
      extra.visibility_state = document.visibilityState;
    }
  } catch {
    /* ignore */
  }

  const tel = getUrlSearchTelemetry();
  if (props.url_search_key_count === undefined) {
    extra.url_search_key_count = tel.key_count;
  }
  if (props.url_has_utm === undefined) {
    extra.url_has_utm = tel.has_utm;
  }
  if (props.url_has_gclid === undefined) {
    extra.url_has_gclid = tel.has_gclid;
  }
  if (props.url_has_fbclid === undefined) {
    extra.url_has_fbclid = tel.has_fbclid;
  }

  if (Object.keys(extra).length === 0) return cr;

  return {
    ...cr,
    properties: {
      ...props,
      ...extra,
    },
  };
}

let featureFlagsSessionMarkerSent = false;

/** Call from PostHog `loaded` — stamps session props when flags are ready (experiments / MCP). */
export function registerPostHogFeatureFlagsReadyListener(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  posthogInstance: any,
) {
  if (!posthogInstance || typeof posthogInstance.onFeatureFlags !== "function") {
    return;
  }

  posthogInstance.onFeatureFlags(() => {
    if (featureFlagsSessionMarkerSent) return;
    featureFlagsSessionMarkerSent = true;
    try {
      posthogInstance.register_for_session({
        feature_flags_client_ready_at: new Date().toISOString(),
      });
    } catch {
      /* ignore */
    }
  });
}

// ─── Person Profile Initialization ───────────────────────────────────────────

/**
 * Call once on PostHog `loaded` callback.
 *
 * PostHog JS already sets automatically (do NOT duplicate):
 *   $browser, $browser_version, $os, $device_type, $browser_language
 *   $screen_height/width, $viewport_height/width
 *   $referrer, $referring_domain, $initial_referrer, $initial_current_url
 *   $host / $initial_host, path & URL variants (UI labels: "Initial/Latest *")
 *   utm_* (last-touch), $initial_utm_* (first-touch); paid ids: gclid, fbclid, ttclid, …
 *   $geoip_country_code/name, $geoip_city_name, $geoip_time_zone, lat/lon, postal, …
 *
 * We only set properties that PostHog does NOT capture automatically.
 * Uses $set (updated each visit) and $set_once (set only the first time).
 */
export function initPersonPropertiesPostHog() {
  if (!isPostHogConfigured()) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const connection = (navigator as any).connection;

  // ── Session / visit count ──────────────────────────────────────────────────
  // Increment total session count stored in localStorage (persists across tabs).
  // Use sessionStorage flag to count once per browser session, not per page load.
  const SESSION_STARTED_KEY = "ph_session_started";
  const TOTAL_SESSIONS_KEY  = "ph_total_sessions";
  const FIRST_VISIT_KEY     = "ph_first_visit";

  const isNewSession = !sessionStorage.getItem(SESSION_STARTED_KEY);
  if (isNewSession) {
    sessionStorage.setItem(SESSION_STARTED_KEY, "1");
  }
  const totalSessions = isNewSession
    ? parseInt(localStorage.getItem(TOTAL_SESSIONS_KEY) || "0") + 1
    : parseInt(localStorage.getItem(TOTAL_SESSIONS_KEY) || "1");
  if (isNewSession) localStorage.setItem(TOTAL_SESSIONS_KEY, String(totalSessions));

  const firstVisitDate = localStorage.getItem(FIRST_VISIT_KEY) || new Date().toISOString();
  if (!localStorage.getItem(FIRST_VISIT_KEY)) {
    localStorage.setItem(FIRST_VISIT_KEY, firstVisitDate);
  }
  const daysSinceFirstVisit = Math.floor(
    (Date.now() - new Date(firstVisitDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  // $set — updated on every session
  const setProps: Record<string, unknown> = {
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
    cookies_enabled: navigator.cookieEnabled,
    do_not_track: navigator.doNotTrack === "1",
    // Network Information API — not captured by PostHog
    connection_type: connection?.effectiveType || null,
    connection_downlink_mbps: connection?.downlink || null,
    connection_rtt_ms: typeof connection?.rtt === "number" ? connection.rtt : null,
    save_data: typeof connection?.saveData === "boolean" ? connection.saveData : null,
    // Device / locale — useful with GeoIP (e.g. es_US on site in AR)
    device_pixel_ratio: typeof window.devicePixelRatio === "number" ? window.devicePixelRatio : null,
    hardware_concurrency:
      typeof navigator.hardwareConcurrency === "number"
        ? navigator.hardwareConcurrency
        : null,
    max_touch_points:
      typeof navigator.maxTouchPoints === "number" ? navigator.maxTouchPoints : null,
    navigator_languages:
      Array.isArray(navigator.languages) && navigator.languages.length > 0
        ? navigator.languages.join(",")
        : navigator.language || null,
    in_app_browser: detectInAppBrowserLabel(),
    // Visit engagement
    session_count: totalSessions,
    days_since_first_visit: daysSinceFirstVisit,
    is_returning_visitor: totalSessions > 1,
  };

  // $set_once — written only when the property doesn't yet exist on the profile
  const setOnceProps: Record<string, unknown> = {
    first_seen: firstVisitDate,
    first_landing_page: window.location.pathname,
    first_user_region: localStorage.getItem("user_region") || "LATAM",
  };

  posthog.setPersonProperties(setProps, setOnceProps);
}

// ─── UTM Attribution ──────────────────────────────────────────────────────────

/**
 * Fire a custom utm_attribution event whenever UTM params are detected.
 * PostHog already sets utm_* person properties automatically — we don't
 * duplicate that here, we just fire the event as a funnel signal.
 */
export function captureUTMAttributionPostHog(
  search: string,
  pathname: string
) {
  if (!isPostHogConfigured()) return;

  const utmParams = parseUTMFromSearch(search);
  if (Object.keys(utmParams).length === 0) return;

  posthog.capture("utm_attribution", {
    ...utmParams,
    page_path: pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── Web Vitals ───────────────────────────────────────────────────────────────

/**
 * Observe Core Web Vitals (LCP, FCP, CLS, TTFB, INP) via PerformanceObserver
 * and send them to PostHog. Call once after PostHog is loaded.
 *
 * Key fix: LCP and CLS are captured on `visibilitychange` (page hide) so we
 * always get the final definitive value, not an intermediate one.
 */
export function trackWebVitalsPostHog() {
  if (!isPostHogConfigured() || typeof PerformanceObserver === "undefined") return;

  const capture = (name: string, value: number, extras?: Record<string, unknown>) => {
    posthog.capture("web_vital", {
      metric_name: name,
      metric_value: Math.round(name === "CLS" ? value * 1000 : value),
      metric_unit: name === "CLS" ? "score_x1000" : "ms",
      page_path: window.location.pathname,
      ...extras,
    });
  };

  // LCP – capture the last (largest) candidate on page hide, not on every update
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let lastLcp: any = null;
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      lastLcp = entries[entries.length - 1];
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && lastLcp) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        capture("LCP", (lastLcp as any).startTime, {
          element_tag: (lastLcp as { element?: Element }).element?.tagName,
        });
        lcpObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // FCP – fires once, safe to capture immediately
  try {
    const fcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          capture("FCP", entry.startTime);
          fcpObserver.disconnect();
        }
      }
    });
    fcpObserver.observe({ type: "paint", buffered: true });
  } catch { /* unsupported */ }

  // CLS – accumulate shifts, capture final value on page hide
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(entry as any).hadRecentInput) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          clsValue += (entry as any).value;
        }
      }
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        if (clsValue > 0) capture("CLS", clsValue);
        clsObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // TTFB + DOM timings – available immediately from navigation entry
  try {
    const navObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const nav = entry as PerformanceNavigationTiming;
        if (nav.responseStart > 0) {
          capture("TTFB", nav.responseStart - nav.requestStart);
          capture("DOM_Interactive", nav.domInteractive);
          capture("DOM_Complete", nav.domComplete);
          navObserver.disconnect();
        }
      }
    });
    navObserver.observe({ type: "navigation", buffered: true });
  } catch { /* unsupported */ }

  // INP – Interaction to Next Paint; track worst interaction per session
  try {
    let worstInp = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let worstInpEntry: any = null;
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = entry as any;
        if (e.duration > worstInp) {
          worstInp = e.duration;
          worstInpEntry = e;
        }
      }
    });
    inpObserver.observe({ type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && worstInpEntry) {
        capture("INP", worstInpEntry.duration, {
          interaction_type: worstInpEntry.name,
        });
        inpObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }
}

/**
 * Track button clicks with PostHog.
 * Note: $browser, $os, $screen_*, $viewport_*, $browser_language are
 * auto-added by PostHog SDK to every event — no need to repeat them here.
 * user_region and test_country are registered as super properties.
 */
export function trackButtonClickPostHog(
  buttonText: string,
  buttonId: string,
  buttonType: string,
  service?: string,
  countryCode?: string,
  templateType?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("button_click", {
    button_text: buttonText,
    button_id: buttonId,
    button_type: buttonType,
    service: service || "general",
    page_path: window.location.pathname,
    page_title: document.title,
    country_code: countryCode || localStorage.getItem("test_country") || null,
    template_type: templateType,
    is_biolink_user: templateType === "BioLinkTemplate",
    timestamp: new Date().toISOString(),
  });

  if (buttonType.includes("modal_open") || buttonType.includes("payment")) {
    posthog.capture("conversion_funnel_step", {
      funnel_step: "modal_open",
      service: service || "general",
      button_text: buttonText,
      country_code: countryCode || localStorage.getItem("test_country") || null,
      template_type: templateType,
    });
  }

  if (buttonType.includes("tally_form")) {
    posthog.capture("form_start", {
      form_type: "tally",
      form_id: buttonId.includes("tally") ? "contact_form" : "unknown",
      button_text: buttonText,
      page_path: window.location.pathname,
    });
  }
}

/**
 * Track payment modal interactions with PostHog
 */
export function trackPaymentModalPostHog(
  action: "open" | "close" | "method_select",
  service: string,
  method?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("payment_modal_interaction", {
    action: action,
    service: service,
    payment_method: method || "unknown",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  // Track conversion funnel
  if (action === "method_select") {
    posthog.capture("conversion_funnel_step", {
      event_category: "Conversion",
      event_label: `${service} - Payment Method Selected`,
      funnel_step: "payment_method_selected",
      service: service,
      payment_method: method,
      country_code: localStorage.getItem("test_country") || "LATAM",
    });
  }
}

/**
 * Track template/layout usage with PostHog.
 * browser_language, timezone, is_mobile are auto-captured by PostHog SDK.
 */
export function trackTemplateUsagePostHog(
  template: string,
  region: string,
  features: string[],
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("template_usage", {
    template,
    region,
    features,
    page_path: window.location.pathname,
    page_title: document.title,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track WhatsApp broadcast channel click (Mercado Pago - 6999 ARS)
 * For conversion and revenue attribution
 */
export function trackWhatsAppBroadcastPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("whatsapp_broadcast_click", {
    revenue: 6999,
    currency: "ARS",
    service: "whatsapp_broadcast",
    page_path: window.location.pathname,
    template: "BaseTemplate",
    checkout_url: "/whatsapp-broadcast",
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/**
 * Track social media link clicks with PostHog
 */
export function trackSocialClickPostHog(platform: string, linkUrl: string) {
  if (!isPostHogConfigured()) return;

  posthog.capture("social_click", {
    platform: platform.toLowerCase(),
    link_url: linkUrl,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/**
 * Track photo interactions with PostHog
 */
export function trackPhotoInteractionPostHog(
  action: "click" | "view" | "lightbox_open" | "lightbox_close",
  photoId: string | number,
  photoTitle?: string,
  pagePath?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("photo_interaction", {
    action: action,
    photo_id: String(photoId),
    photo_title: photoTitle || `Photo ${photoId}`,
    page_path: pagePath || window.location.pathname,
    timestamp: new Date().toISOString(),
    template_type:
      localStorage.getItem("user_region") === "US"
        ? "BioLinkTemplate"
        : "BaseTemplate",
  });
}

/**
 * Track video/reel interactions with PostHog
 */
export function trackVideoInteractionPostHog(
  action: "click" | "play" | "pause" | "complete",
  videoId: string | number,
  videoTitle?: string,
  embedId?: string,
  pagePath?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_interaction", {
    action: action,
    video_id: String(videoId),
    video_title: videoTitle || `Video ${videoId}`,
    embed_id: embedId,
    page_path: pagePath || window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/**
 * Track internal link clicks with PostHog
 */
export function trackLinkClickPostHog(linkText: string, linkUrl: string) {
  if (!isPostHogConfigured()) return;

  posthog.capture("link_click", {
    link_text: linkText,
    link_url: linkUrl,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    is_internal:
      linkUrl.startsWith("/") || linkUrl.includes(window.location.hostname),
  });
}

/**
 * Track page views with enhanced data.
 * When on "/", pass template so session replays can be filtered by template.
 *
 * Note: PostHog JS auto-adds to every event: $current_url, $browser, $os,
 * $screen_*, $viewport_*, $referrer, $browser_language, $device_type, GeoIP.
 * `user_region` / `test_country` come from registerSuperPropertiesPostHog() — omit here to avoid duplicate props.
 *
 * @param routeMeta optional classification (vip, premium_invite, …) from extractPostHogRouteMeta().
 */
export function trackPageViewPostHog(
  pagePath?: string,
  pageTitle?: string,
  template?: string | null,
  routeMeta?: PostHogRouteMeta | null,
) {
  if (!isPostHogConfigured()) return;

  const payload: Record<string, unknown> = {
    // Standard $pageview properties PostHog expects
    $current_url: window.location.href,
    // Custom properties — not auto-captured by PostHog JS
    path: pagePath || window.location.pathname,
    title: pageTitle || document.title,
    hash: window.location.hash,
    timestamp: new Date().toISOString(),
    // Browser timezone (more precise than GeoIP tz for local-time analysis)
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  if (template) payload.template = template;
  if (routeMeta) {
    payload.route_type = routeMeta.route_type;
    if (routeMeta.slug) payload.slug = routeMeta.slug;
  }

  posthog.capture("$pageview", payload);
}

/**
 * Track invitacion-mejores-amigos (Best Friends Instagram) / grupo-privado (Telegram Groups) page events (view, CTA click, close).
 * Use page_path in props to distinguish routes; defaults to /invitacion-mejores-amigos.
 */
export function trackInvitacionGrupoPostHog(
  action: "view" | "cta_click" | "close",
  props?: {
    page_path?: string;
    cta_url?: string;
    protection_violation?: string;
    [key: string]: unknown; // Allow additional properties for protection tracking
  },
) {
  if (!isPostHogConfigured()) return;

  const pagePath =
    props?.page_path ??
    (props as { current_path?: string })?.current_path ??
    "/invitacion-mejores-amigos";

  posthog.capture("invitacion_mejores_amigos", {
    ...props,
    action,
    page_path: pagePath,
    page_url: window.location.href,
    referrer: document.referrer,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track scroll depth
 */
export function trackScrollDepthPostHog(scrollPercentage: number) {
  if (!isPostHogConfigured()) return;

  posthog.capture("scroll_depth", {
    scroll_percentage: scrollPercentage,
    page_url: window.location.href,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track time spent on page
 */
export function trackTimeOnPagePostHog(timeSpentSeconds: number) {
  if (!isPostHogConfigured()) return;

  posthog.capture("time_on_page", {
    time_spent_seconds: timeSpentSeconds,
    page_url: window.location.href,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track user identification.
 * browser, language, timezone, screen_resolution are auto-captured by PostHog.
 */
export function identifyUserPostHog(
  userId: string,
  properties?: Record<string, unknown>,
) {
  if (!isPostHogConfigured()) return;

  posthog.identify(userId, {
    ...properties,
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
  });
}

/**
 * Set user properties
 */
export function setUserPropertiesPostHog(properties: Record<string, unknown>) {
  if (!isPostHogConfigured()) return;

  posthog.setPersonProperties({
    ...properties,
    last_updated: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country"),
  });
}

/**
 * Track conversion events
 */
export function trackConversionPostHog(
  conversionType: string,
  service: string,
  value?: number,
  currency?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("conversion", {
    conversion_type: conversionType,
    service: service,
    value: value,
    currency: currency || "USD",
    page_path: window.location.pathname,
    user_region: localStorage.getItem("user_region") || "LATAM",
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track errors and exceptions.
 * user_agent is auto-captured by PostHog SDK on every event.
 */
export function trackErrorPostHog(error: Error, context?: Record<string, unknown>) {
  if (!isPostHogConfigured()) return;

  posthog.capture("$exception", {
    $exception_message: error.message,
    $exception_type: error.name,
    $exception_stack_trace_raw: error.stack,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...context,
  });
}

/**
 * Start session recording
 */
export function startSessionRecordingPostHog() {
  if (!isPostHogConfigured()) return;

  try {
    posthog.startSessionRecording();
  } catch {
    // intentional
  }
}

/**
 * Stop session recording
 */
export function stopSessionRecordingPostHog() {
  if (!isPostHogConfigured()) return;

  try {
    posthog.stopSessionRecording();
  } catch {
    // intentional
  }
}

/**
 * Check if session recording is active
 */
export function isSessionRecordingActivePostHog(): boolean {
  if (!isPostHogConfigured()) return false;

  try {
    return posthog.sessionRecordingStarted();
  } catch (error) {
    return false;
  }
}

/**
 * Get session replay URL (always canonical UI host + numeric project id).
 */
export function getSessionReplayUrlPostHog(): string | null {
  if (!isPostHogConfigured()) return null;

  try {
    const raw = posthog.get_session_replay_url?.() ?? null;
    if (isCanonicalPostHogReplayUrl(raw)) return String(raw).trim();
    const sid = (posthog as { get_session_id?: () => string }).get_session_id?.();
    return buildCanonicalPostHogReplayUrl(sid);
  } catch {
    return null;
  }
}

/** Distinct id + replay link for Slack / server mirror payloads (cheap at capture time). */
export function getPostHogCorrelationIdsPostHog(): {
  ph_distinct_id: string | null;
  ph_session_replay_url: string | null;
} {
  if (!isPostHogConfigured()) {
    return { ph_distinct_id: null, ph_session_replay_url: null };
  }
  try {
    const ph = posthog as {
      get_distinct_id?: () => string;
      get_session_id?: () => string;
      get_session_replay_url?: () => string | null;
    };
    const distinct = ph.get_distinct_id?.() ?? null;
    const sid = ph.get_session_id?.() ?? null;
    const sdkReplay = ph.get_session_replay_url?.() ?? null;
    // Prefer canonical UI link. SDK with api_host=/ingest often returns
    // `/ingest/project/phc_…/replay/…` which is useless for humans + Slack.
    const replay = isCanonicalPostHogReplayUrl(sdkReplay)
      ? String(sdkReplay).trim()
      : buildCanonicalPostHogReplayUrl(sid);
    return {
      ph_distinct_id: distinct,
      ph_session_replay_url: replay,
    };
  } catch {
    return { ph_distinct_id: null, ph_session_replay_url: null };
  }
}

/**
 * Headers so first-party API handlers (or a future BFF) can merge activity with the same
 * PostHog person + session as the browser SDK. Use on `fetch` to `/api/*` when the body
 * might omit ids (e.g. `sendBeacon` cannot attach custom headers — put ids in the JSON body).
 * @see https://posthog.com/docs/libraries/js
 */
export function getPostHogRequestHeadersPostHog(): Record<string, string> {
  if (!isPostHogConfigured()) return {};
  const out: Record<string, string> = {};
  try {
    const ph = posthog as {
      get_distinct_id?: () => string;
      get_session_id?: () => string;
    };
    const did = ph.get_distinct_id?.();
    if (did) out["X-POSTHOG-DISTINCT-ID"] = did;
    const sid = ph.get_session_id?.();
    if (sid) out["X-POSTHOG-SESSION-ID"] = sid;
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Registers premium-funnel identifiers on every captured event until unregister.
 */
export function registerBioPremiumFunnelContextPostHog(params: {
  bio_premium_flow_trace_id: string;
  bio_premium_tab_session_id: string;
  template_funnel: string;
}) {
  if (!isPostHogConfigured()) return;
  try {
    posthog.register({
      bio_premium_flow_trace_id: params.bio_premium_flow_trace_id,
      bio_premium_tab_session_id: params.bio_premium_tab_session_id,
      template_funnel: params.template_funnel,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Track feature flag usage
 */
export function trackFeatureFlagPostHog(flagName: string, flagValue: unknown) {
  if (!isPostHogConfigured()) return;

  posthog.capture("feature_flag_used", {
    flag_name: flagName,
    flag_value: flagValue,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Get feature flag value
 */
export function getFeatureFlagPostHog(flagName: string): unknown {
  if (!isPostHogConfigured()) return null;

  try {
    return posthog.getFeatureFlag(flagName);
  } catch (error) {
    return null;
  }
}

/**
 * Check if feature is enabled
 */
export function isFeatureEnabledPostHog(featureName: string): boolean {
  if (!isPostHogConfigured()) return false;

  try {
    return posthog.isFeatureEnabled(featureName);
  } catch (error) {
    return false;
  }
}

/**
 * Debug PostHog status
 */
export function debugPostHogStatus() {
  if (typeof window === "undefined") {
    return;
  }
}

/**
 * Track BioTemplatePremium discount offer flow
 */
export function trackDiscountOfferShownPostHog(
  flowName: string,
  planTier: string,
  originalPrice: number,
  discountedPrice: number,
  currency: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("discount_offer_shown", {
    flow_name: flowName,
    plan_tier: planTier,
    original_price: originalPrice,
    discounted_price: discountedPrice,
    discount_percentage: 10,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackDiscountOfferActionPostHog(
  flowName: string,
  action: "accepted" | "declined",
  planTier: string,
  originalPrice: number,
  discountedPrice: number,
  currency: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("discount_offer_action", {
    flow_name: flowName,
    action,
    plan_tier: planTier,
    original_price: originalPrice,
    discounted_price: discountedPrice,
    discount_percentage: 10,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackVipVideoOpenedPostHog(
  flowName: string,
  playbackId: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_video_opened", {
    flow_name: flowName,
    playback_id: playbackId.substring(0, 20) + "...",
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackVipVideoClosedPostHog(
  flowName: string,
  playbackId: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_video_closed", {
    flow_name: flowName,
    playback_id: playbackId.substring(0, 20) + "...",
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation page view
 */
export function trackDonationPageViewPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_page_view", {
    page: "sorprendeme",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation amount selection
 */
export function trackDonationAmountSelectedPostHog(
  amount: number,
  isCustom: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_amount_selected", {
    amount: amount,
    is_custom_amount: isCustom,
    page: "sorprendeme",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation form submission
 */
export function trackDonationFormSubmittedPostHog(
  amount: number,
  hasName: boolean,
  messageLength: number
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_form_submitted", {
    amount: amount,
    has_name: hasName,
    message_length: messageLength,
    is_anonymous: !hasName,
    page: "sorprendeme",
    funnel_step: "form_submitted",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation payment success
 */
export function trackDonationPaymentSuccessPostHog(
  amount: number,
  donationId: string | null
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_payment_success", {
    amount: amount,
    donation_id: donationId,
    page: "sorprendeme",
    funnel_step: "payment_completed",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });

  // También trackear como conversión
  posthog.capture("conversion", {
    conversion_type: "donation",
    conversion_value: amount,
    currency: "ARS",
    page: "sorprendeme",
  });
}

/**
 * Track donation payment error
 */
export function trackDonationPaymentErrorPostHog(
  amount: number,
  errorMessage: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_payment_error", {
    amount: amount,
    error_message: errorMessage,
    page: "sorprendeme",
    funnel_step: "payment_failed",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation checkout started
 */
export function trackDonationCheckoutStartedPostHog(
  amount: number,
  preferenceId: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_checkout_started", {
    amount: amount,
    preference_id: preferenceId,
    page: "sorprendeme",
    funnel_step: "checkout_opened",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

// ─── Media Gallery Tracking (BioLinkTemplate) ─────────────────────────────────

export function trackMediaItemClickPostHog(
  mediaId: string | number,
  mediaType: "video" | "photo",
  mediaTitle?: string,
  positionInGallery?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("media_item_click", {
    media_id: String(mediaId),
    media_type: mediaType,
    media_title: mediaTitle || `Media ${mediaId}`,
    position_in_gallery: positionInGallery,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackLightboxOpenedPostHog(
  mediaId: string | number,
  source: "gallery_click" | "direct_link",
  mediaType?: "video" | "photo",
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_opened", {
    media_id: String(mediaId),
    source,
    media_type: mediaType,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackLightboxClosedPostHog(
  mediaId: string | number,
  timeSpentSeconds: number,
  viewedItemsCount?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_closed", {
    media_id: String(mediaId),
    time_spent_seconds: timeSpentSeconds,
    viewed_items_count: viewedItemsCount,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackLightboxNavigationPostHog(
  direction: "prev" | "next",
  fromMediaId: string | number,
  toMediaId: string | number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_navigation", {
    direction,
    from_media_id: String(fromMediaId),
    to_media_id: String(toMediaId),
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Surfaces for `base_template_ui_click` (header, footer, lightbox, etc.) */
export type BaseTemplateClickSurface =
  | "header"
  | "nav_desktop"
  | "nav_mobile"
  | "mobile_menu"
  | "hero"
  | "sobre_mi"
  | "footer"
  | "lightbox";

export type BaseTemplateAnalyticsName = "BaseTemplate" | "BaseTemplateBlack";

export function trackBaseTemplateUIClickPostHog(params: {
  templateName: BaseTemplateAnalyticsName;
  elementId: string;
  label: string;
  surface: BaseTemplateClickSurface;
  href?: string;
  countryCode?: string;
  extra?: Record<string, string | number | boolean | null | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("base_template_ui_click", {
    template_type: params.templateName,
    element_id: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    country_code:
      params.countryCode ||
      localStorage.getItem("test_country") ||
      undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    ...params.extra,
  });
}

// ─── Video Analytics ─────────────────────────────────────────────────────────

export function trackVideoPlayPostHog(
  videoId: string | number,
  videoTitle?: string,
  playbackId?: string,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_play", {
    video_id: String(videoId),
    video_title: videoTitle || `Video ${videoId}`,
    playback_id: playbackId ? playbackId.substring(0, 20) + "..." : undefined,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVideoPausePostHog(
  videoId: string | number,
  currentTimeSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_pause", {
    video_id: String(videoId),
    current_time_seconds: currentTimeSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVideoCompletePostHog(
  videoId: string | number,
  durationSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_complete", {
    video_id: String(videoId),
    duration_seconds: durationSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVideoProgressPostHog(
  videoId: string | number,
  progressPercent: number,
  currentTimeSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_progress", {
    video_id: String(videoId),
    progress_percent: progressPercent,
    current_time_seconds: currentTimeSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── BioTemplatePremium Checkout Flow ────────────────────────────────────────

export function trackPlanButtonClickPostHog(
  planTier: "standard" | "vip" | "free",
  price: number,
  currency: string,
  flowName: string,
  country?: string,
  hasVideo?: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("plan_button_click", {
    plan_tier: planTier,
    price,
    currency,
    flow_name: flowName,
    country_code: country,
    has_video: hasVideo,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackCheckoutPanelOpenedPostHog(
  planTier: "standard" | "vip",
  source: "direct_button" | "video_cta" | "discount_accepted",
  price: number,
  currency: string,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_panel_opened", {
    plan_tier: planTier,
    source,
    price,
    currency,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  mirrorFunnelEvent({
    event_name: "checkout_panel_opened",
    template: "BioTemplatePremium",
    flow_name: flowName ?? "bio_template_premium",
    country,
    landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    props: {
      plan_tier: planTier,
      source,
      price,
      currency,
    },
  }, { capturePosthog: false });
}

export function trackCheckoutPanelClosedPostHog(
  planTier: "standard" | "vip",
  emailProvided: boolean,
  timeInCheckoutSeconds: number,
  reason: "user_closed" | "completed" | "error",
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_panel_closed", {
    plan_tier: planTier,
    email_provided: emailProvided,
    time_in_checkout_seconds: timeInCheckoutSeconds,
    reason,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackEmailInputInteractionPostHog(
  action: "focus" | "blur",
  planTier?: "standard" | "vip",
  hasValue?: boolean,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("email_input_interaction", {
    action,
    plan_tier: planTier,
    has_value: hasValue,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackEmailValidationErrorPostHog(
  errorType: "empty" | "invalid",
  planTier?: "standard" | "vip",
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("email_validation_error", {
    error_type: errorType,
    plan_tier: planTier,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackCheckoutAbandonmentPostHog(
  planTier: "standard" | "vip",
  emailProvided: boolean,
  timeInCheckoutSeconds: number,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_abandonment", {
    plan_tier: planTier,
    email_provided: emailProvided,
    time_in_checkout_seconds: timeInCheckoutSeconds,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── Purchase Revenue Tracking ───────────────────────────────────────────────

export function trackPurchaseWithRevenuePostHog(
  purchaseId: string,
  revenue: number,
  currency: string,
  planTier?: string,
  planName?: string,
  paymentMethod?: string,
  isSubscription?: boolean,
  subscriptionId?: string,
  userId?: string,
  country?: string,
  stripeSessionId?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("purchase_with_revenue", {
    purchase_id: purchaseId,
    revenue,
    currency: currency.toUpperCase(),
    plan_tier: planTier,
    plan_name: planName,
    payment_method: paymentMethod,
    is_subscription: isSubscription,
    subscription_id: subscriptionId,
    user_id: userId,
    country_code: country,
    stripe_session_id: stripeSessionId,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  trackConversionPostHog("purchase", "premium_subscription", revenue, currency);
}

export function trackSubscriptionStartedPostHog(
  subscriptionId: string,
  planTier: string,
  price: number,
  currency: string,
  country?: string,
  userId?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("subscription_started", {
    subscription_id: subscriptionId,
    plan_tier: planTier,
    price,
    currency: currency.toUpperCase(),
    country_code: country,
    user_id: userId,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── User Identification ─────────────────────────────────────────────────────

/**
 * Identify user by email (called at checkout when email is provided).
 * PostHog SDK auto-captures browser, language, timezone, screen_resolution.
 * Calling identify() links all prior anonymous events to this person profile.
 */
export function identifyUserByEmailPostHog(
  email: string,
  properties?: Record<string, unknown>
) {
  if (!isPostHogConfigured()) return;

  posthog.identify(email, {
    email,
    ...properties,
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
  });
}

// ─── MediaKit Tracking (/mediakit, /mediakit-intro) ──────────────────────────

export type MediaKitVariant = "intro" | "desktop";
export type MediaKitNavMethod = "button" | "keyboard" | "swipe" | "thumbnail";

/** Fired once when the MediaKit viewer mounts */
export function trackMediaKitViewPostHog(
  variant: MediaKitVariant,
  totalPages: number,
  isFullscreenMode: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_view", {
    variant,
    total_pages: totalPages,
    is_fullscreen_mode: isFullscreenMode,
    page_path: window.location.pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    is_mobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
  });
}

/** Fired when the password gate is shown to a visitor */
export function trackMediaKitGateShownPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_gate_shown", {
    page_path: window.location.pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Fired on each password submission */
export function trackMediaKitPasswordAttemptPostHog(success: boolean) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_password_attempt", {
    success,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Fired every time the visible page changes */
export function trackMediaKitPageViewPostHog(
  pageNumber: number,
  pageTitle: string,
  totalPages: number,
  navigationMethod: MediaKitNavMethod,
  isFullscreen: boolean,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_page_view", {
    page_number: pageNumber,
    page_title: pageTitle,
    total_pages: totalPages,
    navigation_method: navigationMethod,
    is_fullscreen: isFullscreen,
    is_first_page: pageNumber === 1,
    is_last_page: pageNumber === totalPages,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Fired when user navigates between pages */
export function trackMediaKitNavigationPostHog(
  direction: "next" | "prev" | "jump",
  method: MediaKitNavMethod,
  fromPage: number,
  toPage: number,
  totalPages: number,
  isFullscreen: boolean,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_navigation", {
    direction,
    navigation_method: method,
    from_page: fromPage,
    to_page: toPage,
    total_pages: totalPages,
    is_fullscreen: isFullscreen,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Fired when entering or exiting fullscreen */
export function trackMediaKitFullscreenTogglePostHog(
  action: "open" | "close",
  currentPage: number,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_fullscreen", {
    action,
    current_page: currentPage,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Fired when the PDF download link is clicked */
export function trackMediaKitDownloadClickedPostHog(variant: MediaKitVariant) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_download_clicked", {
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "mediakit_download",
    variant,
    page_path: window.location.pathname,
  });
}

/** Fired once when all pages have been seen */
export function trackMediaKitCompletedPostHog(
  totalPages: number,
  timeSpentSeconds: number,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_completed", {
    total_pages: totalPages,
    time_spent_seconds: timeSpentSeconds,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

/** Fired when the user leaves the mediakit (unmount), with total session time */
export function trackMediaKitSessionEndPostHog(
  totalPages: number,
  pagesViewed: number,
  timeSpentSeconds: number,
  maxPageReached: number,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_session_end", {
    total_pages: totalPages,
    pages_viewed: pagesViewed,
    pages_viewed_pct: Math.round((pagesViewed / totalPages) * 100),
    max_page_reached: maxPageReached,
    time_spent_seconds: timeSpentSeconds,
    completed: pagesViewed === totalPages,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

// ─── VIP Custom Payment Funnel (/vip/:nombre) ────────────────────────────────

export function trackVipPageViewPostHog(
  nombre: string,
  country: string,
  isAvailable: boolean,
  paymentMethod: "mercadopago" | "stripe"
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_page_view", {
    vip_nombre: nombre,
    country_code: country,
    is_available: isAvailable,
    payment_method: paymentMethod,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
    is_mobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
  });
}

export function trackVipUnavailablePostHog(
  nombre: string,
  country: string,
  paymentMethod: "mercadopago" | "stripe"
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_unavailable_shown", {
    vip_nombre: nombre,
    country_code: country,
    payment_method: paymentMethod,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVipStepChangePostHog(
  nombre: string,
  from: string,
  to: string,
  country: string,
  currency: "ARS" | "USD",
  amount?: number
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_step_change", {
    vip_nombre: nombre,
    from_step: from,
    to_step: to,
    country_code: country,
    currency,
    amount,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVipAmountSelectedPostHog(
  nombre: string,
  amount: number,
  currency: "ARS" | "USD",
  country: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_amount_selected", {
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });
}

export function trackVipCheckoutStartedPostHog(
  nombre: string,
  amount: number,
  currency: "ARS" | "USD",
  country: string,
  paymentMethod: "mercadopago" | "stripe",
  hasEmail: boolean,
  hasName: boolean,
  hasMessage: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_checkout_started", {
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    has_email: hasEmail,
    has_name: hasName,
    has_message: hasMessage,
    funnel_step: "checkout_initiated",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "vip_checkout_started",
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
  });
}

export function trackVipPaymentResultPostHog(
  nombre: string,
  result: "success" | "error" | "pending",
  amount: number,
  currency: "ARS" | "USD",
  country: string,
  paymentMethod: "mercadopago" | "stripe",
  donationId?: string | null,
  errorMessage?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_payment_result", {
    vip_nombre: nombre,
    result,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    donation_id: donationId,
    error_message: result === "error" ? errorMessage : undefined,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: localStorage.getItem("user_region") || "LATAM",
  });

  if (result === "success") {
    posthog.capture("conversion", {
      conversion_type: "vip_payment",
      value: amount,
      currency,
      vip_nombre: nombre,
      country_code: country,
      payment_method: paymentMethod,
      donation_id: donationId,
    });
  }
}

const STRIPE_PAY_FAILURE_MSG_MAX = 500;

export type StripePayFailureStage =
  | "stripe_js_unavailable"
  | "payment_intent_create"
  | "checkout_session_create"
  | "payment_confirm"
  | "invalid_config"
  | "flow_blocked"
  | "checkout_redirect_failed"
  | "checkout_flow_error";

/**
 * Classify Stripe failure for dashboards (ops vs real card declines).
 * 60d intel: July stripe_pay_failure was mostly expired rk_live, not WTP.
 */
export function classifyStripeFailureKind(opts: {
  failure_kind?: string;
  error_message?: string;
  stripe_error_code?: string;
  stripe_error_type?: string;
  decline_code?: string;
  http_status?: number;
}): string {
  if (opts.failure_kind && opts.failure_kind !== "unknown") {
    return opts.failure_kind;
  }
  const msg = String(opts.error_message || "").toLowerCase();
  const code = String(opts.stripe_error_code || "").toLowerCase();
  const type = String(opts.stripe_error_type || "").toLowerCase();
  const decline = String(opts.decline_code || "").toLowerCase();

  if (
    code === "api_key_expired" ||
    msg.includes("expired api key") ||
    msg.includes("api key expired") ||
    (msg.includes("expired") && (msg.includes("rk_") || msg.includes("sk_") || msg.includes("api key")))
  ) {
    return "expired_api_key";
  }
  if (
    msg.includes("restricted key") ||
    msg.includes("does not have the required permissions") ||
    code === "secret_key_required"
  ) {
    return "restricted_key";
  }
  if (decline || code === "card_declined" || msg.includes("card was declined")) {
    return decline ? `card_declined_${decline}` : "card_declined";
  }
  if (code === "insufficient_funds" || decline === "insufficient_funds") {
    return "insufficient_funds";
  }
  if (code === "expired_card" || decline === "expired_card") {
    return "expired_card";
  }
  if (type === "card_error") return "card_error";
  if (type === "invalid_request_error" || code === "resource_missing") {
    return "invalid_request";
  }
  if (type === "authentication_error" || opts.http_status === 401) {
    return "auth_error";
  }
  if (opts.http_status === 429) return "rate_limited";
  if (opts.http_status && opts.http_status >= 500) return "stripe_server_error";
  if (msg.includes("network") || msg.includes("fetch")) return "network_or_exception";
  if (code) return `stripe_${code}`;
  return opts.failure_kind || "unknown";
}

/**
 * Use one event for dashboards: breakdown by `stage`, `flow_surface`, `stripe_error_code`, `failure_kind`.
 */
export function trackStripePayFailurePostHog(props: {
  stage: StripePayFailureStage;
  /** e.g. checkout_channel_invite_page, mux_video_cta, bio_template_premium_simple */
  flow_surface: string;
  failure_kind?: string;
  error_message?: string;
  http_status?: number;
  stripe_error_code?: string;
  stripe_error_type?: string;
  decline_code?: string;
  amount_cents?: number;
  currency?: string;
  pack_id?: string;
  plan_tier?: string;
  flow_trace_id?: string;
  /** Optional Stripe request/param for ops triage */
  stripe_param?: string;
  request_id?: string;
}) {
  if (!isPostHogConfigured()) return;

  const msg = props.error_message
    ? String(props.error_message).slice(0, STRIPE_PAY_FAILURE_MSG_MAX)
    : undefined;

  const failure_kind = classifyStripeFailureKind({
    failure_kind: props.failure_kind,
    error_message: msg,
    stripe_error_code: props.stripe_error_code,
    stripe_error_type: props.stripe_error_type,
    decline_code: props.decline_code,
    http_status: props.http_status,
  });

  // Ops signal: key/config failures are not willingness-to-pay
  const is_ops_failure =
    failure_kind === "expired_api_key" ||
    failure_kind === "restricted_key" ||
    failure_kind === "auth_error" ||
    failure_kind === "invalid_config";

  const eventProps = {
    stage: props.stage,
    flow_surface: props.flow_surface,
    failure_kind,
    is_ops_failure,
    error_message: msg,
    http_status: props.http_status,
    stripe_error_code: props.stripe_error_code || undefined,
    stripe_error_type: props.stripe_error_type || undefined,
    decline_code: props.decline_code || undefined,
    stripe_param: props.stripe_param
      ? String(props.stripe_param).slice(0, 80)
      : undefined,
    request_id: props.request_id
      ? String(props.request_id).slice(0, 64)
      : undefined,
    amount_cents: props.amount_cents,
    currency: props.currency,
    pack_id: props.pack_id,
    plan_tier: props.plan_tier,
    flow_trace_id: props.flow_trace_id,
    site: BRAND_SOURCE_KEY,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
  };

  posthog.capture("stripe_pay_failure", eventProps);

  // Mirror to funnel_events + Slack so payment failures surface in admin dashboards
  mirrorFunnelEvent({
    event_name: "stripe_pay_failure",
    landing_path: window.location.pathname,
    props: eventProps,
  }, { capturePosthog: false }); // PostHog already captured above
}

export function getPostHogStripeMetadataPostHog(): Record<string, string> {
  const c = getPostHogCorrelationIdsPostHog();
  const out: Record<string, string> = {};
  if (c.ph_distinct_id) out.ph_distinct_id = c.ph_distinct_id.slice(0, 256);
  if (c.ph_session_replay_url) {
    out.ph_session_replay_url = c.ph_session_replay_url.slice(0, 512);
  }
  return out;
}

export function capturePostPurchaseProfileCompletedPostHog(payload: {
  plan_tier: string | null;
  from_vip: boolean;
  from_basic: boolean;
  from_standard?: boolean;
  stripe_session_id: string | null;
  mercadopago_subscription_id?: string | null;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("post_purchase_profile_completed", {
    plan_tier: payload.plan_tier || undefined,
    from_vip: payload.from_vip,
    from_basic: payload.from_basic,
    from_standard: payload.from_standard ?? false,
    has_stripe_session: Boolean(payload.stripe_session_id),
    stripe_session_id: payload.stripe_session_id || undefined,
    has_mercadopago_subscription: Boolean(payload.mercadopago_subscription_id),
    mercadopago_subscription_id: payload.mercadopago_subscription_id || undefined,
    page_path: window.location.pathname,
  });
}

/** Funnel stage for section/CTA/revenue dashboards (PostHog + Slack). */
export type SurfaceFunnelStage =
  | "awareness"
  | "interest"
  | "intent"
  | "checkout"
  | "purchase"
  | "retention";

export type SurfaceFunnelTrackParams = {
  eventName: string;
  sectionId?: string;
  ctaId?: string;
  funnelStage?: SurfaceFunnelStage;
  /** Estimated USD if this step converts (for revenue funnel dashboards). */
  revenuePotentialUsd?: number;
  template?: string;
  templateVariant?: string;
  flowName?: string;
  country?: string;
  landingPath?: string;
  sessionId?: string;
  props?: Record<string, unknown>;
};

/**
 * Canonical section/CTA funnel capture — PostHog + optional mirror payload shape.
 * Use for dashboards (section_id, cta_id, funnel_stage, revenue_potential_usd).
 */
export function buildSurfaceFunnelProperties(
  params: SurfaceFunnelTrackParams,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    ...(params.props && typeof params.props === "object" ? params.props : {}),
  };
  if (params.sectionId) out.section_id = params.sectionId;
  if (params.ctaId) out.cta_id = params.ctaId;
  if (params.funnelStage) out.funnel_stage = params.funnelStage;
  if (
    params.revenuePotentialUsd !== undefined &&
    Number.isFinite(params.revenuePotentialUsd)
  ) {
    out.revenue_potential_usd = params.revenuePotentialUsd;
  }
  if (params.template) out.template = params.template;
  if (params.templateVariant) out.template_variant = params.templateVariant;
  if (params.flowName) out.flow_name = params.flowName;
  if (params.country) out.country = params.country;
  if (params.landingPath) out.landing_path = params.landingPath;
  if (params.sessionId) out.funnel_session_id = params.sessionId;
  return out;
}

/** Fire a named funnel event with standardized section/CTA/revenue props in PostHog. */
export function captureSurfaceFunnelPostHog(params: SurfaceFunnelTrackParams): void {
  if (!isPostHogConfigured()) return;
  try {
    posthog.capture(params.eventName, buildSurfaceFunnelProperties(params));
  } catch {
    /* noop */
  }
}

/** One impression per section per session (for section funnel dashboards). */
const surfaceSectionSeen = new Set<string>();

export function trackSurfaceSectionViewPostHog(
  sectionId: string,
  params: Omit<SurfaceFunnelTrackParams, "eventName" | "sectionId"> & {
    eventName?: string;
  },
): void {
  if (typeof window === "undefined") return;
  const sessionKey = params.sessionId || "anon";
  const dedupeKey = `${sessionKey}:${sectionId}`;
  if (surfaceSectionSeen.has(dedupeKey)) return;
  surfaceSectionSeen.add(dedupeKey);

  captureSurfaceFunnelPostHog({
    ...params,
    eventName: params.eventName || "surface_section_view",
    sectionId,
    funnelStage: params.funnelStage || "awareness",
  });
}

/** Explicit CTA click with section + revenue context. */
export function trackSurfaceCtaClickPostHog(
  ctaId: string,
  sectionId: string,
  params: Omit<SurfaceFunnelTrackParams, "eventName" | "sectionId" | "ctaId"> & {
    eventName?: string;
  },
): void {
  captureSurfaceFunnelPostHog({
    ...params,
    eventName: params.eventName || "surface_cta_click",
    sectionId,
    ctaId,
    funnelStage: params.funnelStage || "intent",
  });
}

// Export PostHog instance for direct access
export { posthog };
